import { useState, useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook for managing evaluation modal state and interactions
 */
export const useEvaluationModal = (result, onClose, onPrevious, onNext, hasPrevious, hasNext) => {
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);

  // State for coach context sections (using semantic keys)
  const [expandedCoachStates, setExpandedCoachStates] = useState({
    history: false,
    summaries: false,
    ltm: false,
  });
  
  // State for judge context sections
  const [expandedJudgeStates, setExpandedJudgeStates] = useState({
    judge_history: false,
    judge_ltm: false,
  });

  // Check if all coach sections are expanded
  const allExpandedCoach = Object.values(expandedCoachStates).every(Boolean);
  
  // Check if all judge sections are expanded
  const allExpandedJudge = Object.values(expandedJudgeStates).every(Boolean);

  const toggleCoachSection = useCallback((key, newState) => {
    setExpandedCoachStates((prev) => ({
      ...prev,
      [key]: newState !== undefined ? newState : !prev[key],
    }));
  }, []);

  const toggleJudgeSection = useCallback((key, newState) => {
    setExpandedJudgeStates((prev) => ({
      ...prev,
      [key]: newState !== undefined ? newState : !prev[key],
    }));
  }, []);

  const toggleAllCoach = useCallback(() => {
    const newState = !allExpandedCoach;
    setExpandedCoachStates({
      history: newState,
      summaries: newState,
      ltm: newState,
    });
  }, [allExpandedCoach]);

  const toggleAllJudge = useCallback(() => {
    const newState = !allExpandedJudge;
    setExpandedJudgeStates({
      judge_history: newState,
      judge_ltm: newState,
    });
  }, [allExpandedJudge]);

  // Keyboard navigation
  useEffect(() => {
    if (!result) return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && hasPrevious) {
        e.preventDefault();
        onPrevious();
      } else if (e.key === 'ArrowRight' && hasNext) {
        e.preventDefault();
        onNext();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [result, onClose, onPrevious, onNext, hasPrevious, hasNext]);

  // Focus trap: Store previous focus and restore on unmount
  useEffect(() => {
    if (result) {
      previousFocusRef.current = document.activeElement;
      // Focus the modal
      if (modalRef.current) {
        const firstFocusable = modalRef.current.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (firstFocusable) {
          firstFocusable.focus();
        }
      }
    } else {
      // Restore focus when modal closes
      if (previousFocusRef.current && previousFocusRef.current.focus) {
        previousFocusRef.current.focus();
      }
    }
  }, [result]);

  // Focus trap: Keep focus within modal
  useEffect(() => {
    if (!result || !modalRef.current) return;

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    modalRef.current.addEventListener('keydown', handleTabKey);
    return () => {
      if (modalRef.current) {
        modalRef.current.removeEventListener('keydown', handleTabKey);
      }
    };
  }, [result]);

  return {
    modalRef,
    expandedCoachStates,
    expandedJudgeStates,
    allExpandedCoach,
    allExpandedJudge,
    toggleCoachSection,
    toggleJudgeSection,
    toggleAllCoach,
    toggleAllJudge,
  };
};

